/**
 * What the pane keeps from an analyze_email result — the extraction fields and
 * the PDF/attachment text — stored WITH the mail item it was analysed for
 * (bug 28610).
 *
 * The pinned pane outlives Office ItemChanged, so anything it stashes from an
 * analysis is still in memory when the AM selects the next email. Held as two
 * bare pane-level values, it seeded THAT email's launch: TEST instance 067046f9
 * sent email B's body with email A's PDF text, and the form filled with another
 * client's mortgagee, loan and policy at "high" confidence.
 *
 * Same guard as `useEmailContextState`: the value carries the identity it
 * belongs to, and a reader only sees it while that identity is still the
 * selection. Keyed on `emailId`, the item id `useClientContext` already resets
 * on, so the stash and the client tile change email together. A blank
 * attachment text is the safe miss — workflow_prefill recovers the email's OWN
 * text from its stored analysis row.
 */

export type SelectedItemAnalysis = {
  /** `EmailContext.emailId` of the item this analysis was run for. */
  itemKey: string;
  extraction: Record<string, unknown> | null;
  attachmentText: string | null;
};

/**
 * Record an analysis for `itemKey`. A field left `undefined` was not reported
 * by this result, so it keeps the SAME item's earlier value — never another
 * item's. No key, nothing stored: an analysis that cannot say which email it
 * belongs to must not seed any launch.
 */
export function recordItemAnalysis(
  prev: SelectedItemAnalysis | null,
  itemKey: string | null | undefined,
  update: {
    extraction?: Record<string, unknown> | null;
    attachmentText?: string | null;
  },
): SelectedItemAnalysis | null {
  if (!itemKey) return null;
  const sameItem = prev?.itemKey === itemKey ? prev : null;
  return {
    itemKey,
    extraction:
      update.extraction !== undefined ? update.extraction : sameItem?.extraction ?? null,
    attachmentText:
      update.attachmentText !== undefined
        ? update.attachmentText
        : sameItem?.attachmentText ?? null,
  };
}

/** The stash, only while its item is the selected one. */
export function analysisForSelectedItem(
  stash: SelectedItemAnalysis | null,
  selectedItemKey: string | null | undefined,
): SelectedItemAnalysis | null {
  return stash && selectedItemKey && stash.itemKey === selectedItemKey ? stash : null;
}
